import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { normalizeResultsResponse } from "features/results/utils/normalizeResultsResponse";

const DEFAULT_LIMIT = 20;

// Unified patient results via the common endpoint scoped by patientId, narrowed
// by messageTypes (re-fetched per selected type filter). No messageTypes → BE
// returns every type for the patient.
const buildUrl = ({
  tenantVal,
  patientId,
  orderBy,
  messageTypes,
  limit,
  offset,
  searchQuery,
  unreviewed,
}) => {
  const params = new URLSearchParams();

  params.set("patientId", String(patientId));
  if (orderBy) params.set("orderBy", orderBy);
  // Server-side pagination: BE returns this page + a `totalCount`.
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  params.set("includeXsl", "true");
  // Server-side search (BE requires >= 3 chars; the caller enforces this).
  if (searchQuery) params.set("searchQuery", searchQuery);
  // Server-side "hide reviewed" filter: omit = all, true = only non-CONFIRMED.
  if (unreviewed !== undefined) params.set("unreviewed", String(unreviewed));
  // Repeated `messageTypes` params — values can contain commas, so never comma-join.
  (messageTypes ?? []).forEach((type) => params.append("messageTypes", type));

  return `${tenantVal}/healthlink/common/results?${params.toString()}`;
};

export const usePatientResults = ({
  patientId,
  orderBy,
  messageTypes,
  searchQuery,
  unreviewed,
  limit = DEFAULT_LIMIT,
  offset = 0,
  enabled = true,
}) => {

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [
      "patientResults",
      orderBy,
      tenantVal,
      patientId,
      messageTypes,
      searchQuery,
      unreviewed,
      limit,
      offset,
    ],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        buildUrl({
          tenantVal,
          patientId,
          orderBy,
          messageTypes,
          limit,
          offset,
          searchQuery,
          unreviewed,
        })
      );

      return normalizeResultsResponse(response.data);
    },
    meta: { errorMessage: "Error fetching patient results" },
   
    staleTime: 1000,
    enabled: enabled && !!patientId && !!tenantVal,
  });
};
